import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { Call4uAudio } from 'src/app/models/Call4uAudio';
import { Call4uAudioService } from 'src/app/shared/services/call4u-audio.service';
import { ConfirmationService } from 'src/app/shared/components/confirmation-modal/confirmation.service';
import { ToastService } from 'src/app/shared/components/toast/toast.service';

@Component({
  selector: 'app-call4u-audios',
  templateUrl: './call4u-audios.component.html',
  styleUrls: ['./call4u-audios.component.scss']
})
export class Call4uAudiosComponent implements OnInit, OnDestroy {
  audios: Call4uAudio[] = [];
  loading = false;
  subscriptions: Subscription[] = [];

  // Player
  currentAudio: HTMLAudioElement | null = null;
  playingId: string | null = null;

  constructor(
    private audioService: Call4uAudioService,
    private confirmationService: ConfirmationService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadAudios();
  }

  ngOnDestroy(): void {
    this.stopAudio();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  loadAudios(): void {
    this.loading = true;
    this.subscriptions.push(
      this.audioService.list()
        .pipe(finalize(() => this.loading = false))
        .subscribe({
          next: (response) => this.audios = response.data,
          error: (err) => this.toastService.error(err.error?.message || 'Erro ao carregar áudios')
        })
    );
  }

  // Player
  togglePlay(audio: Call4uAudio): void {
    if (this.playingId === audio.id) {
      this.stopAudio();
      return;
    }

    this.stopAudio();

    if (!audio.file_url) {
      this.toastService.error('URL do áudio não disponível');
      return;
    }

    this.currentAudio = new Audio(audio.file_url);
    this.playingId = audio.id;

    this.currentAudio.addEventListener('ended', () => {
      this.playingId = null;
      this.currentAudio = null;
    }, { once: true });

    this.currentAudio.play().catch(() => {
      this.toastService.error('Erro ao reproduzir áudio');
      this.playingId = null;
      this.currentAudio = null;
    });
  }

  stopAudio(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    this.playingId = null;
  }

  isPlaying(audioId: string): boolean {
    return this.playingId === audioId;
  }

  // Exclusão
  confirmDelete(audio: Call4uAudio): void {
    this.confirmationService.show(
      `Deseja realmente excluir o áudio "${audio.title}"?`,
      () => this.deleteAudio(audio.id)
    );
  }

  deleteAudio(id: string): void {
    this.subscriptions.push(
      this.audioService.delete(id).subscribe({
        next: () => {
          this.toastService.success('Áudio excluído com sucesso!');
          this.stopAudio();
          this.loadAudios();
        },
        error: (err) => this.toastService.error(err.error?.message || 'Erro ao excluir áudio')
      })
    );
  }

  // Helpers
  formatDuration(seconds: number): string {
    if (!seconds) return '--:--';
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      'pending': 'Pendente',
      'processing': 'Processando',
      'approved': 'Aprovado',
      'rejected': 'Rejeitado',
      'error': 'Erro'
    };
    return labels[status] || status;
  }

  getStatusClass(status: string): string {
    const classes: Record<string, string> = {
      'pending': 'badge bg-warning text-dark',
      'processing': 'badge bg-info',
      'approved': 'badge bg-success',
      'rejected': 'badge bg-danger',
      'error': 'badge bg-danger'
    };
    return classes[status] || 'badge bg-secondary';
  }

  getTypeLabel(type: string): string {
    return type === 'tts' ? 'TTS (IA)' : 'Upload';
  }
}
